import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DialogService } from '../../../../shared/services/dialog.service';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';
import { ICrossFieldRule, ICrossFieldRulePatch, RuleSeverity } from '../../models/validation-rule';

/**
 * Simplified rule editor used by the Location Hierarchy tab.
 *
 * Lets the admin tune severity, is_active, message_template, and sort_order
 * on an auto-generated CROSS_FIELD_VALUE_DEPENDENCY rule. The rule's
 * `params.allowed_target_values` (the country list / state list / county
 * list) is intentionally rendered read-only because it is managed by the
 * `_locations.py` dataset and `seed_validation_rules` command upstream.
 */
@Component({
  selector: 'app-location-rule-editor-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './location-rule-editor-modal.component.html',
})
export class LocationRuleEditorModalComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() modalId!: string;
  @Input() rule: ICrossFieldRule | null = null;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('dialog') dialogEl!: ElementRef<HTMLDialogElement>;

  private fb = inject(FormBuilder);
  private facade = inject(ValidationRulesFacade);
  private dialog = inject(DialogService);

  form!: FormGroup;
  readonly SEVERITY_OPTIONS: RuleSeverity[] = ['ERROR', 'WARNING'];

  ngAfterViewInit(): void {
    if (this.modalId && this.dialogEl?.nativeElement) {
      this.dialog.register(this.modalId, this.dialogEl.nativeElement);
    }
    this.initForm();
  }

  ngOnDestroy(): void {
    if (this.modalId) this.dialog.unregister(this.modalId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rule'] && this.form) {
      this.initForm();
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      severity: [
        (this.rule?.severity as RuleSeverity) ?? 'ERROR',
        Validators.required,
      ],
      is_active: [this.rule?.is_active ?? true],
      message_template: [this.rule?.message_template ?? ''],
      sort_order: [this.rule?.sort_order ?? 0],
    });
  }

  sourceValue(): string {
    return String((this.rule?.params as { source_value?: unknown })?.source_value ?? '');
  }

  allowedValues(): string[] {
    return (
      (this.rule?.params as { allowed_target_values?: string[] | undefined })
        ?.allowed_target_values ?? []
    );
  }

  close(): void {
    this.closed.emit();
  }

  submit(): void {
    if (!this.rule || !this.form || this.form.invalid) return;
    const changes: ICrossFieldRulePatch = {
      severity: this.form.value.severity,
      is_active: !!this.form.value.is_active,
      message_template: this.form.value.message_template ?? '',
      sort_order: Number(this.form.value.sort_order ?? 0),
    };
    this.facade.patchCrossFieldRule(this.rule.id, changes);
    this.close();
  }
}
